import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import {
  DeliveryChallan,
  CreateDeliveryChallanRequest,
  CreateStandaloneDCRequest,
  UpdateDeliveryChallanRequest,
  DispatchDeliveryChallanRequest,
  DeliveryChallanFilters,
} from '@/types/subcontracting';

/**
 * Hook for managing Delivery Challans in the subcontracting workflow
 * Provides CRUD operations and workflow state management
 */
export const useDeliveryChallan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Fetch all delivery challans with optional filters
   */
  const fetchDeliveryChallans = useCallback(async (filters?: DeliveryChallanFilters) => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('delivery_challans_with_details')
        .select('*')
        .order('dc_date', { ascending: false });

      // Apply filters
      if (filters?.vendor_id) {
        query = query.eq('vendor_id', filters.vendor_id);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.date_from) {
        query = query.gte('dc_date', filters.date_from);
      }
      if (filters?.date_to) {
        query = query.lte('dc_date', filters.date_to);
      }
      if (filters?.search_term) {
        query = query.or(`dc_number.ilike.%${filters.search_term}%,vendor_name.ilike.%${filters.search_term}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      return data as DeliveryChallan[];
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to fetch delivery challans: ${err.message}`,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Fetch a single delivery challan by ID with lines
   */
  const fetchDeliveryChallanById = useCallback(async (dc_id: string) => {
    setLoading(true);
    setError(null);

    try {
      // Fetch DC header
      const { data: dcData, error: dcError } = await supabase
        .from('delivery_challans_with_details')
        .select('*')
        .eq('id', dc_id)
        .single();

      if (dcError) throw dcError;

      // Fetch DC lines
      const { data: linesData, error: linesError } = await supabase
        .from('delivery_challan_lines')
        .select('*')
        .eq('dc_id', dc_id)
        .order('created_at', { ascending: true });

      if (linesError) throw linesError;

      console.log('fetchDeliveryChallanById - lines data:', linesData?.map(l => ({ item_code: l.item_code, qty_sent: l.qty_sent, qty_received: l.qty_received })));

      return {
        ...dcData,
        lines: linesData,
      } as DeliveryChallan;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to fetch delivery challan: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Generate next DC number
   */
  const generateDCNumber = useCallback(async (dcType: string = 'SUBCONTRACT'): Promise<string | null> => {
    try {
      // Must pass dc_type_param to avoid function ambiguity
      const { data, error } = await supabase.rpc('generate_dc_number', { dc_type_param: dcType });

      if (error) throw error;

      return data as string;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to generate DC number: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  /**
   * Create a new delivery challan
   */
  const createDeliveryChallan = useCallback(async (request: CreateDeliveryChallanRequest): Promise<DeliveryChallan | null> => {
    setLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Generate DC number
      const dc_number = await generateDCNumber();
      if (!dc_number) throw new Error('Failed to generate DC number');

      // Create DC header
      const { data: dcData, error: dcError } = await supabase
        .from('delivery_challans')
        .insert({
          dc_number,
          sub_contract_id: request.sub_contract_id,
          vendor_id: request.vendor_id,
          vendor_name: request.vendor_name,
          dc_date: request.dc_date || new Date().toISOString().split('T')[0], // Use provided date or current date
          expected_return_date: request.expected_return_date || null, // Convert empty string to null
          transport_mode: request.transport_mode,
          vehicle_number: request.vehicle_number,
          driver_name: request.driver_name,
          driver_contact: request.driver_contact,
          notes: request.notes,
          status: 'Draft',
          created_by: user.id,
        })
        .select()
        .single();

      if (dcError) throw dcError;

      // Create DC lines
      if (request.lines && request.lines.length > 0) {
        const linesData = request.lines.map(line => ({
          dc_id: dcData.id,
          sub_contract_line_id: line.sub_contract_line_id,
          item_code: line.item_code,
          item_description: line.item_description,
          routing_operation_id: line.routing_operation_id,
          operation_name: line.operation_name,
          qty_sent: line.qty_sent,
          uom: line.uom,
          line_notes: line.line_notes,
        }));

        const { error: linesError } = await supabase
          .from('delivery_challan_lines')
          .insert(linesData);

        if (linesError) throw linesError;
      }

      toast({
        title: 'Success',
        description: `Delivery Challan ${dc_number} created successfully`,
      });

      // Fetch and return the complete DC with lines
      return await fetchDeliveryChallanById(dcData.id);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to create delivery challan: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast, generateDCNumber, fetchDeliveryChallanById]);

  /**
   * Update delivery challan details
   */
  const updateDeliveryChallan = useCallback(async (
    dc_id: string,
    request: UpdateDeliveryChallanRequest
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('delivery_challans')
        .update({
          expected_return_date: request.expected_return_date,
          transport_mode: request.transport_mode,
          vehicle_number: request.vehicle_number,
          driver_name: request.driver_name,
          driver_contact: request.driver_contact,
          notes: request.notes,
        })
        .eq('id', dc_id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Delivery Challan updated successfully',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to update delivery challan: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Dispatch delivery challan to vendor
   */
  const dispatchDeliveryChallan = useCallback(async (request: DispatchDeliveryChallanRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Build update object with transport details if provided
      const updateData: Record<string, any> = {
        status: 'Dispatched',
        dispatch_date: request.dispatch_date || new Date().toISOString(),
        dispatched_by: user.id,
      };

      // Add transport details if provided
      if (request.transport_mode !== undefined) updateData.transport_mode = request.transport_mode;
      if (request.vehicle_number !== undefined) updateData.vehicle_number = request.vehicle_number;
      if (request.driver_name !== undefined) updateData.driver_name = request.driver_name;
      if (request.driver_phone !== undefined) updateData.driver_phone = request.driver_phone;
      if (request.notes !== undefined) updateData.notes = request.notes;

      // Update DC status to Dispatched with transport details
      const { error: dcError } = await supabase
        .from('delivery_challans')
        .update(updateData)
        .eq('id', request.dc_id);

      if (dcError) throw dcError;

      // Update related subcontract status to Dispatched
      const { data: dcData } = await supabase
        .from('delivery_challans')
        .select('sub_contract_id')
        .eq('id', request.dc_id)
        .single();

      if (dcData?.sub_contract_id) {
        const { error: scError } = await supabase
          .from('sub_contracts')
          .update({
            status: 'Dispatched',
            dispatch_date: request.dispatch_date || new Date().toISOString(),
          })
          .eq('id', dcData.sub_contract_id);

        if (scError) throw scError;
      }

      toast({
        title: 'Success',
        description: 'Delivery Challan dispatched successfully',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to dispatch delivery challan: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Delete delivery challan (only if status is Draft)
   */
  const deleteDeliveryChallan = useCallback(async (dc_id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Check if DC is in Draft status
      const { data: dcData, error: checkError } = await supabase
        .from('delivery_challans')
        .select('status, dc_number')
        .eq('id', dc_id)
        .single();

      if (checkError) throw checkError;

      if (dcData.status !== 'Draft') {
        throw new Error('Only draft delivery challans can be deleted');
      }

      // Delete DC (lines will be cascade deleted)
      const { error: deleteError } = await supabase
        .from('delivery_challans')
        .delete()
        .eq('id', dc_id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Success',
        description: `Delivery Challan ${dcData.dc_number} deleted successfully`,
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to delete delivery challan: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Create a standalone delivery challan (not linked to a subcontract)
   * Used for general job work scenarios
   */
  const createStandaloneDeliveryChallan = useCallback(async (
    request: CreateStandaloneDCRequest
  ): Promise<DeliveryChallan | null> => {
    setLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Generate DC number
      const dc_number = await generateDCNumber();
      if (!dc_number) throw new Error('Failed to generate DC number');

      // Create DC header (without sub_contract_id)
      const { data: dcData, error: dcError } = await supabase
        .from('delivery_challans')
        .insert({
          dc_number,
          sub_contract_id: null, // Standalone DC
          vendor_id: request.vendor_id,
          vendor_name: request.vendor_name,
          dc_date: request.dc_date || new Date().toISOString().split('T')[0],
          expected_return_date: request.expected_return_date || null,
          transport_mode: request.transport_mode,
          vehicle_number: request.vehicle_number,
          driver_name: request.driver_name,
          driver_contact: request.driver_contact,
          notes: request.notes,
          project_id: request.project_id || null,
          bom_id: request.bom_id || null,
          status: 'Draft',
          created_by: user.id,
        })
        .select()
        .single();

      if (dcError) throw dcError;

      // Create DC lines (without sub_contract_line_id)
      if (request.lines && request.lines.length > 0) {
        const linesData = request.lines.map(line => ({
          dc_id: dcData.id,
          sub_contract_line_id: null, // Standalone line
          inventory_item_id: line.inventory_item_id || null,
          item_code: line.item_code,
          item_description: line.item_description,
          operation_name: line.operation_name || null,
          qty_sent: line.qty_sent,
          uom: line.uom,
          unit_price: line.unit_price || null,
          total_price: line.total_price || null,
          line_notes: line.line_notes,
        }));

        const { error: linesError } = await supabase
          .from('delivery_challan_lines')
          .insert(linesData);

        if (linesError) throw linesError;
      }

      toast({
        title: 'Success',
        description: `Standalone Delivery Challan ${dc_number} created successfully`,
      });

      // Fetch and return the complete DC with lines
      return await fetchDeliveryChallanById(dcData.id);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to create standalone DC: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast, generateDCNumber, fetchDeliveryChallanById]);

  /**
   * Fetch DC lines with remaining quantities for partial receipt
   * Returns lines that still have quantity available to receive
   */
  const fetchDCLinesForReceipt = useCallback(async (dc_id: string) => {
    try {
      const { data: linesData, error: linesError } = await supabase
        .from('delivery_challan_lines')
        .select('*, sub_contract_line_id')
        .eq('dc_id', dc_id)
        .order('created_at', { ascending: true });

      if (linesError) throw linesError;

      // Calculate remaining quantities
      return (linesData || []).map(line => ({
        ...line,
        qty_received: line.qty_received || 0,
        qty_remaining: Math.max(0, (line.qty_sent || 0) - (line.qty_received || 0)),
      }));
    } catch (err: any) {
      console.error('Error fetching DC lines for receipt:', err);
      return [];
    }
  }, []);

  /**
   * Check if DC has any remaining quantity to receive
   */
  const hasPendingReceipt = useCallback(async (dc_id: string): Promise<boolean> => {
    try {
      const lines = await fetchDCLinesForReceipt(dc_id);
      return lines.some(line => line.qty_remaining > 0);
    } catch {
      return false;
    }
  }, [fetchDCLinesForReceipt]);

  return {
    loading,
    error,
    fetchDeliveryChallans,
    fetchDeliveryChallanById,
    fetchDCLinesForReceipt,
    hasPendingReceipt,
    generateDCNumber,
    createDeliveryChallan,
    createStandaloneDeliveryChallan,
    updateDeliveryChallan,
    dispatchDeliveryChallan,
    deleteDeliveryChallan,
  };
};
